// src/lib/api/products.ts
import type { Product } from "@/types/product";

function mapApiProductToFrontend(p: any): Product {
  return {
    id: p.id ?? p.Id,
    slug: p.slug ?? p.Slug,
    name: p.name ?? p.Name,

    price: Number(p.price ?? p.Price ?? 0),
    compareAtPrice: p.compareAtPrice ?? p.CompareAtPrice ?? null,

    brand: p.brand ?? p.Brand ?? null,
    categorySlug: p.categorySlug ?? p.CategorySlug ?? null,
    condition: p.condition ?? p.Condition ?? null,
    description: p.description ?? p.Description ?? null,
    inventoryLocation: p.inventoryLocation ?? p.InventoryLocation ?? null,

    quantity: Number(p.quantity ?? p.Quantity ?? 0),

    images: (p.images ?? p.Images ?? []).map((img: any) => ({
      id: img.id ?? img.Id,
      url: img.url ?? img.Url,
      alt: img.alt ?? img.Alt ?? null,
      sortOrder: Number(img.sortOrder ?? img.SortOrder ?? 0),
    })),
  };
}

export async function getProductBySlug(slug: string): Promise<Product | null> {
  // ✅ Call the proxy route you actually have: /api/products/[slug]
  const res = await fetch(`/api/products/${encodeURIComponent(slug)}`, {
    cache: "no-store",
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`getProductBySlug failed: ${res.status}`);

  const data = await res.json();
  return mapApiProductToFrontend(data);
}

export async function getProducts(params?: {
  q?: string;
  brand?: string[];
  cond?: string[];
  category?: string[];
  min?: number;
  max?: number;
  sort?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ items: Product[]; page: number; pageSize: number; total: number }> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.brand?.length) qs.set("brand", params.brand.join(","));
  if (params?.cond?.length) qs.set("cond", params.cond.join(","));
  if (params?.category?.length) qs.set("category", params.category.join(","));
  if (typeof params?.min === "number") qs.set("min", String(params.min));
  if (typeof params?.max === "number") qs.set("max", String(params.max));
  if (params?.sort) qs.set("sort", params.sort);
  if (params?.page) qs.set("page", String(params.page));
  if (params?.pageSize) qs.set("pageSize", String(params.pageSize));

  const url = `/api/products${qs.toString() ? `?${qs}` : ""}`;

  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`getProducts failed: ${res.status}`);

  const data = await res.json();
  return {
    items: (data.items ?? data.Items ?? []).map(mapApiProductToFrontend),
    page: data.page ?? data.Page ?? 1,
    pageSize: data.pageSize ?? data.PageSize ?? 24,
    total: data.total ?? data.Total ?? 0,
  };
}
